import React from 'react';
import { Box, Paper, Typography, Skeleton, Divider, Alert } from '@mui/material';
import { useTemplateBuilder } from './TemplateBuilderContext';

const TemplateLoading: React.FC = () => {
  const { tasksState, processingError } = useTemplateBuilder();

  // Derived state from tasksState
  const isProcessingTranscript = tasksState.transcriptAnalysis === 'processing';
  const isFileProcessingCompleted = tasksState.fileProcessing === 'completed';
  const isWaitingForTranscript =
    isFileProcessingCompleted && tasksState.transcriptSelection === 'idle';

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: theme => `1px solid ${theme.palette.divider}`,
        height: '100%',
      }}
    >
      <Typography variant="h6" gutterBottom>
        Template loading
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {isProcessingTranscript
          ? 'Processing transcript and generating template structure...'
          : isWaitingForTranscript
            ? 'File processed. Please select a transcript to continue.'
            : 'Template preview will appear here after selecting a transcript'}
      </Typography>

      {processingError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {processingError}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Skeleton variant="text"  sx={{ width: "60%", height: 32 }}/>
        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: 'rgba(144, 238, 144, 0.2)',
            borderRadius: 1,
          }}
        >
          <Skeleton variant="text"  sx={{ width: "100%" }}/>
          <Skeleton variant="text"  sx={{ width: "90%" }}/>
          <Skeleton variant="text"  sx={{ width: "80%" }}/>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 3 }}>
        <Skeleton variant="text"  sx={{ width: "40%", height: 32 }}/>
        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: 'rgba(144, 238, 144, 0.2)',
            borderRadius: 1,
          }}
        >
          <Skeleton variant="text"  sx={{ width: "100%" }}/>
          <Skeleton variant="text"  sx={{ width: "85%" }}/>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 3 }}>
        <Skeleton variant="text"  sx={{ width: "50%", height: 32 }}/>
        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: 'rgba(144, 238, 144, 0.2)',
            borderRadius: 1,
          }}
        >
          <Skeleton variant="text"  sx={{ width: "100%" }}/>
          <Skeleton variant="text"  sx={{ width: "95%" }}/>
          <Skeleton variant="text"  sx={{ width: "70%" }}/>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Skeleton variant="rectangular" sx={{width: 24, height: 24,  borderRadius: 0.5 }} />
          <Skeleton variant="text"  sx={{ width: "60%", height: 20 }}/>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Skeleton variant="rectangular" sx={{width: 24, height: 24,  borderRadius: 0.5 }} />
          <Skeleton variant="text"  sx={{ width: "70%", height: 20 }}/>
        </Box>
      </Box>
    </Paper>
  );
};

export default TemplateLoading;
